import { Field, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  LoadingIndicator,
  Button,
  ComboBox,
  TextInput,
  Icons,
} from '@components';
import { useNavigate } from 'react-router-dom';
import { rendaConversaoSchema } from '@schemas/beneficios/rendaConversao';
import { SimularBeneficioService } from '@services/simular-beneficio';
import { currency } from '@utils';
import { format } from 'date-fns';
import {
  mdiCurrencyUsd,
  mdiCalendarRange,
  mdiCommentAlertOutline,
} from '@mdi/js';
import {
  opcoesPercentualSaldo,
  ID_CONJUGADO,
  ID_VITALICIO,
} from '../../../Beneficios/components/Renda/utils';
import {
  resetSimulacaoRenda,
  setSimulacaoRenda,
} from '../../../../store/plan/slice';
import { BENEFICIARIOS_CONVERSAO_ABSOLUTE_PATH } from '../../../../common/utils/paths';

const simularBeneficioService = new SimularBeneficioService();

export const RendaConversaoBeneficios = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const custom = useSelector((state) => state.configs.tema);
  const matricula = useSelector((state) => state.user.data?.matricula);
  const idade = useSelector((state) => state.user.dadosPessoais?.idade);
  const [opRendaConversao, setOpRendaConversao] = useState([]);
  const [acessoConversao, setAcessoConversao] = useState(false);
  const [percentualRCMCadastrado, setPercentualRCMCadastrado] = useState('0');
  const [proporcionalidadeConv, setProporcionalidadeConv] = useState({
    ValorBeneficioAtual: '0',
    ValorSaldoAt49: '0',
    ValorSaldoAt83: '0',
    ValorSaldoTotal: '0',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const verificarCompatibilidadeConversao = async () => {
      const compatibilidadeConversao =
        await simularBeneficioService.getCompatibilidadeConversao(matricula);
      setAcessoConversao(compatibilidadeConversao);
    };

    const obterPercentualRCM = async () => {
      const RCMParticipante = await simularBeneficioService.getPercentualRCM(
        matricula
      );

      setPercentualRCMCadastrado(RCMParticipante);
    };

    const obterProporcionalidadeConversao = async () => {
      const proporcionalidadeConvParticipante =
        await simularBeneficioService.getProporcionalidadeConversao(matricula);
      setProporcionalidadeConv(proporcionalidadeConvParticipante);
    };

    const opcoesTipoRenda = async () => {
      const verificacaoRendaConjugada =
        await simularBeneficioService.getVerificacaoRendaConjugada(matricula);

      setOpRendaConversao(verificacaoRendaConjugada);
    };

    const obterDadosAPI = async () => {
      setLoading(true);
      await verificarCompatibilidadeConversao();
      await obterProporcionalidadeConversao();
      await obterPercentualRCM();
      await opcoesTipoRenda();
      setLoading(false);
    };
    obterDadosAPI();
  }, [matricula]);

  useEffect(() => {
    dispatch(resetSimulacaoRenda());
  }, [dispatch]);

  const atualizarRendaConversao = (rendaAtualizada) => {
    const params = { ...rendaAtualizada, telaSimulacaoConversao: true };
    dispatch(setSimulacaoRenda(params));
    navigate(BENEFICIARIOS_CONVERSAO_ABSOLUTE_PATH);
  };

  return loading ? (
    <LoadingIndicator />
  ) : (
    <>
      <div className="row">
        <div className="col-12 col-md-6 col-lg-4 mb-4">
          <div className="card h-100">
            <div className="card-body">
              <Icons
                path={mdiCalendarRange}
                color={custom.codCorLaranja}
                alt="File Icon"
                size={2}
              />
            </div>
            <div className="card-footer">
              <p className="text-muted ts-14">Idade atual</p>
              <h1 className="ts-20 fw-bold">{`${idade} anos`}</h1>
              <p className="text-muted ts-14">Data base para cálculo</p>
              <h1 className="ts-20 fw-bold">
                {format(new Date(), 'dd/MM/yyyy')}
              </h1>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-4">
          <div className="card h-100">
            <div className="card-body">
              <Icons
                path={mdiCurrencyUsd}
                color={custom.codCorLaranja}
                alt="File Icon"
                size={2}
              />
            </div>
            <div className="card-footer">
              <p className="text-muted ts-14">Valor do benefício atual</p>
              <h1 className="ts-20 fw-bold">
                {currency(proporcionalidadeConv.ValorBeneficioAtual)}
              </h1>
              <p className="text-muted mt-2 ts-14">Valor do saldo total</p>
              <h1 className="ts-20 fw-bold">
                {currency(proporcionalidadeConv.ValorSaldoTotal)}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body pb-3">
          {acessoConversao ? (
            <Formik
              initialValues={{
                tipoRendaMensal:
                  opRendaConversao.length === 1
                    ? opRendaConversao[0].value
                    : '',
                percentualRendaMensal: '',
                percentualrcm:
                  percentualRCMCadastrado === 0 ? '' : percentualRCMCadastrado,
                percentualSaldoVitalicio: '',
              }}
              validationSchema={rendaConversaoSchema}
              onSubmit={(values, { setSubmitting }) => {
                setSubmitting(false);
                atualizarRendaConversao(values);
              }}
              key={percentualRCMCadastrado}
            >
              {({
                handleSubmit,
                isValid,
                values,
                resetForm,
                setFieldValue,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        name="tipoRendaMensal"
                        className="mb-3"
                        component={ComboBox}
                        label="Tipo de renda mensal"
                        onChange={(e) => {
                          const tipoRendaSelecionado = e;
                          resetForm();
                          setFieldValue(
                            'tipoRendaMensal',
                            tipoRendaSelecionado.value
                          );
                        }}
                        options={opRendaConversao.map((x) => {
                          return { value: x.value, label: x.label };
                        })}
                      />

                      {values.tipoRendaMensal.toString() === ID_CONJUGADO && (
                        <Field
                          name="percentualSaldoVitalicio"
                          className="mb-3"
                          component={TextInput}
                          type="number"
                          label="Percentual de saldo em vitalício"
                        />
                      )}
                    </div>
                    <div className="col-md-12 col-lg-6">
                      <Field
                        name="percentualrcm"
                        className="mb-3"
                        type="number"
                        component={TextInput}
                        label="Percentual RCM"
                        disabled={
                          percentualRCMCadastrado !== 0 ||
                          (opRendaConversao.length === 1 &&
                            opRendaConversao[0].value.toString() ===
                              ID_VITALICIO)
                        }
                      />
                      {values.tipoRendaMensal.toString() === ID_CONJUGADO && (
                        <Field
                          name="percentualRendaMensal"
                          className="mb-3"
                          component={TextInput}
                          label="Percentual de renda mensal"
                          options={opcoesPercentualSaldo.map((x) => {
                            return { value: x.value, label: x.label };
                          })}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6" />
                    <div className="col-12 col-md-6">
                      <Button
                        type="submit"
                        className="col-6 justify-items-right"
                        disabled={!isValid}
                      >
                        Continuar
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          ) : (
            <div className="pt-0 pt-md-4 pb-32 d-block d-lg-flex align-items-center justify-content-between">
              <div className="alert-box d-block d-md-flex mb-32 mb-md-4 mb-lg-0">
                <Icons
                  path={mdiCommentAlertOutline}
                  color="var(--primary-color)"
                  size={2}
                  style={{ marginRight: 4 }}
                />
                <div className="ms-0 ms-md-32">
                  <p className="text-black fw-bold ts-20 mb-2">
                    Aviso - Conversão de benefícios
                  </p>
                  <p className="ts-14 text-muted">
                    Você não é elegível para realizar essa simulação
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
